// BaseFlow Local Query Builder

import { BaseFlowLocalClient } from './client';
import { 
  BaseFlowResponse, 
  LocalBuilder, 
  LocalFilterBuilder, 
  LocalQueryBuilder 
} from './types';

/**
 * A thenable class that represents a BaseFlow Local request.
 */
export class LocalRequestBuilder<T = any> implements LocalBuilder<T> {
  protected client: BaseFlowLocalClient;
  protected table: string;
  protected method: string = 'GET';
  protected path: string;
  protected params: Record<string, any> = {};
  protected body: any = null;
  protected headers: Record<string, string> = {};
  protected idFilter: any = null;
  protected isSingle: boolean = false;

  constructor(client: BaseFlowLocalClient, table: string) {
    this.client = client;
    this.table = table;
    this.path = `/api/tables/${table}`;
  }

  /**
   * Executes the request and returns a promise that resolves with the response.
   */
  async then<TResult1 = BaseFlowResponse<T>, TResult2 = never>(
    onfulfilled?: ((value: BaseFlowResponse<T>) => TResult1 | PromiseLike<TResult1>) | undefined | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | undefined | null
  ): Promise<TResult1 | TResult2> {
    try {
      const response = await this.client.request(this.method, this.path, {
        params: this.params,
        body: this.body,
        headers: this.headers
      });
      
      // Unwrap the server response structure
      // Server returns { success: true, data: ..., count, total } but client expects { data: ..., error: ... }
      let finalResponse = response;
      
      if (response.data && typeof response.data === 'object') {
        if ('success' in response.data && 'data' in response.data) {
          // Server wrapped the data with success flag, unwrap it
          let unwrappedData = response.data.data;
          
          // If single() was called and data is an array, return first element
          if (this.isSingle && Array.isArray(unwrappedData)) {
            unwrappedData = unwrappedData.length > 0 ? unwrappedData[0] : null;
          }
          
          finalResponse = {
            data: unwrappedData,
            error: response.error,
            status: response.status,
            statusText: response.statusText
          };
        } else if ('data' in response.data) {
          // Server wrapped the data without success flag, unwrap it
          let unwrappedData = response.data.data;
          
          // If single() was called and data is an array, return first element
          if (this.isSingle && Array.isArray(unwrappedData)) {
            unwrappedData = unwrappedData.length > 0 ? unwrappedData[0] : null;
          }
          
          finalResponse = {
            data: unwrappedData,
            error: response.error,
            status: response.status,
            statusText: response.statusText
          };
        }
      }
      
      if (onfulfilled) {
        return onfulfilled(finalResponse);
      }
      return finalResponse as any;
    } catch (error) {
      if (onrejected) {
        return onrejected(error);
      }
      throw error;
    }
  }
}

/**
 * A builder for creating filters.
 */
export class LocalFilterBuilderImpl<T = any> extends LocalRequestBuilder<T> implements LocalFilterBuilder<T> {
  
  /**
   * Adds an 'equals' filter to the query.
   */
  eq(column: keyof T, value: any): LocalFilterBuilder<T> {
    // Track ID filter for UPDATE operations only
    // DELETE can use filters on the base path
    if (column === 'id' && this.method === 'PUT') {
      this.idFilter = value;
      this.path = `/api/tables/${this.table}/${value}`;
    } else {
      this.params[String(column)] = value;
    }
    return this;
  }

  /**
   * Adds a 'not equals' filter to the query.
   */
  neq(column: keyof T, value: any): LocalFilterBuilder<T> {
    this.params[`${String(column)}[neq]`] = value;
    return this;
  }

  /**
   * Adds a 'greater than' filter to the query.
   */
  gt(column: keyof T, value: any): LocalFilterBuilder<T> {
    this.params[`${String(column)}[gt]`] = value;
    return this;
  }

  /**
   * Adds a 'greater than or equal to' filter to the query.
   */
  gte(column: keyof T, value: any): LocalFilterBuilder<T> {
    this.params[`${String(column)}[gte]`] = value;
    return this;
  }

  /**
   * Adds a 'less than' filter to the query.
   */
  lt(column: keyof T, value: any): LocalFilterBuilder<T> {
    this.params[`${String(column)}[lt]`] = value;
    return this;
  }

  /**
   * Adds a 'less than or equal to' filter to the query.
   */
  lte(column: keyof T, value: any): LocalFilterBuilder<T> {
    this.params[`${String(column)}[lte]`] = value;
    return this;
  }

  /**
   * Adds a 'like' filter to the query.
   */
  like(column: keyof T, pattern: string): LocalFilterBuilder<T> {
    this.params[`${String(column)}[like]`] = pattern;
    return this;
  }

  /**
   * Adds a 'case-insensitive like' filter to the query.
   */
  ilike(column: keyof T, pattern: string): LocalFilterBuilder<T> {
    this.params[`${String(column)}[ilike]`] = pattern;
    return this;
  }

  /**
   * Adds an 'is' filter to the query.
   */
  is(column: keyof T, value: null | boolean): LocalFilterBuilder<T> {
    this.params[String(column)] = value;
    return this;
  }

  /**
   * Adds an 'in' filter to the query.
   */
  in(column: keyof T, values: any[]): LocalFilterBuilder<T> {
    this.params[`${String(column)}[in]`] = values.join(',');
    return this;
  }

  /**
   * Adds a 'match' filter to the query.
   */
  match(query: Record<keyof T, any>): LocalFilterBuilder<T> {
    Object.entries(query).forEach(([key, value]) => {
      this.eq(key as keyof T, value);
    });
    return this;
  }

  /**
   * Adds a filter to the query.
   */
  filter(column: keyof T, operator: string, value: any): LocalFilterBuilder<T> {
    this.params[`${String(column)}[${operator}]`] = value;
    return this;
  }
}

/**
 * A builder for creating queries.
 */
export class LocalQueryBuilderImpl<T = any> extends LocalFilterBuilderImpl<T> implements LocalQueryBuilder<T> {
  
  /**
   * Specifies the columns to select.
   */
  select(columns: string = '*'): LocalQueryBuilder<T> {
    this.params.select = columns;
    return this;
  }

  /**
   * Inserts data into the table.
   */
  insert(data: Partial<T> | Partial<T>[]): LocalRequestBuilder<T> {
    this.method = 'POST';
    this.body = data;
    return this;
  }

  /**
   * Updates data in the table.
   */
  update(data: Partial<T>): LocalFilterBuilder<T> {
    this.method = 'PUT';
    this.body = data;
    return this;
  }

  /**
   * Deletes data from the table.
   */
  delete(): LocalFilterBuilder<T> {
    this.method = 'DELETE';
    return this;
  }

  /**
   * Adds an 'order by' clause to the query.
   */
  order(column: keyof T, options: { ascending?: boolean } = {}): LocalQueryBuilder<T> {
    this.params.sort = String(column);
    this.params.order = options.ascending !== false ? 'asc' : 'desc';
    return this;
  }

  /**
   * Limits the number of rows returned.
   */
  limit(count: number): LocalQueryBuilder<T> {
    this.params.limit = count;
    return this;
  }

  /**
   * Skips a number of rows.
   */
  offset(count: number): LocalQueryBuilder<T> {
    this.params.offset = count;
    return this;
  }

  /**
   * Specifies a range of rows to return.
   */
  range(from: number, to: number): LocalQueryBuilder<T> {
    this.params.limit = to - from + 1;
    this.params.offset = from;
    return this;
  }

  /**
   * Returns a single row from the query.
   */
  single(): LocalFilterBuilder<T> {
    this.params.limit = 1;
    this.isSingle = true;
    return this;
  }
}
